import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { UnifiedTreeItem } from '../../../_shared/models/driver.model';
import { HttpClient } from '@angular/common/http';
import { AssetDrivers } from '../models/vehicle-assignment.model';
import { environment } from '../../../../environments/environment';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Response } from '../../../_shared/models';

@Injectable({
  providedIn: 'root'
})
export class AssignmentFilterService {
  selectedBillingAccountId: Signal<number | null> = signal(null);
  isBillingAccountReadonly: Signal<boolean> = signal(false);
  isSingleAssignment: WritableSignal<boolean> = signal(false);
  selectedDrivers: Signal<{ id: number; name: string }[]> = signal([]);
  assignedAssetDrivers: Signal<AssetDrivers[]> = signal([]);
  selectedFilterType: WritableSignal<string | null> = signal(null);

  constructor(private http: HttpClient) { }

  setAssignmentBillingAccountId(billingAccountId: number) {
    this.isSingleAssignment.set(true);
    (this.selectedBillingAccountId as WritableSignal<number | null>).set(billingAccountId);
  }

  setSelectedBillingAccountId(items: UnifiedTreeItem[]) {
    const selected = items.find(item => (item.type === 'driver' || item.type === 'asset' || item.type === 'account') &&
      item.checked &&
      item.billingAccountId !== null
    );

    if (!this.isBillingAccountReadonly()) {
      (this.selectedBillingAccountId as WritableSignal<number | null>).set(selected?.billingAccountId ?? null);
    }
  }

  setSelectedDrivers(drivers: { id: number; name: string }[]): void {
    (this.selectedDrivers as WritableSignal<{ id: number; name: string }[]>).set(drivers);
  }

  setReadOnlyBillingAccount(billingAccountId: number) {
    (this.isBillingAccountReadonly as WritableSignal<boolean>).set(true);
    (this.selectedBillingAccountId as WritableSignal<number | null>).set(billingAccountId);
  }

  resetBillingAccountId() {
    (this.isBillingAccountReadonly as WritableSignal<boolean>).set(false);
    (this.selectedBillingAccountId as WritableSignal<number | null>).set(null);
  }

  async updateAssignedAssetDrivers(cancellation: Subject<void>): Promise<void> {
    const result = await firstValueFrom(
      this.http
        .get<Response<AssetDrivers[]>>(`${environment.apiEndpoint}/vehicleassignment/asset-drivers/${this.selectedBillingAccountId() ?? 0}`)
        .pipe(takeUntil(cancellation))
    );
    (this.assignedAssetDrivers as WritableSignal<AssetDrivers[]>).set(result.data ?? []);
  }

  driverToAssetsMap() {
    const map = new Map<number, number[]>();
    for (const record of this.assignedAssetDrivers()) {
      const driverId = record.driverId;
      if (!map.has(driverId)) {
        map.set(driverId, []);
      }
      map.get(driverId)?.push(record.assetId);
    }
    return map;
  }

  isDriverAssignedToAsset(assetId: number): boolean {
    const selectedDrivers = this.selectedDrivers();
    const map = this.driverToAssetsMap();

    return selectedDrivers.some(driver => map.get(driver.id)?.includes(assetId));
  }
}